import React, { useState } from "react";
import styled from "styled-components";

import { connect } from "react-redux";

import RegisterUserForm from "./components/RegisterUserForm";
import LoginForm from "./components/LoginForm";
import Snackbar from "../../common/Snackbar";

import images from "../../../resources";

import firebase, { auth } from "../../../database/firebase";
import { bootstrapNewUser } from "../../Root";

const Page = styled.div`
  position: relative;
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-6);
  background: var(--color-bg);
  background-image:
    radial-gradient(
      circle at 20% 0%,
      var(--color-accent-subtle) 0%,
      transparent 40%
    ),
    radial-gradient(
      circle at 80% 100%,
      var(--color-accent-subtle) 0%,
      transparent 50%
    );
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-5);
  width: 100%;
  max-width: 420px;
`;

const LogoLetter = styled.img`
  width: 96px;
  height: 96px;
`;

const Card = styled.div`
  width: 100%;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
  padding: var(--space-8) var(--space-6);
`;

const ChangeTabButton = styled.button`
  background: none;
  border: none;
  padding: var(--space-3) var(--space-4) 0;
  margin: 0 auto;
  display: block;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-accent-text);
  cursor: pointer;
  border-radius: var(--radius-sm);

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const PublicLinks = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: var(--space-4);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);

  a {
    color: var(--color-text-secondary);
  }
`;

const Login = (props: any) => {
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarError, setSnackbarError] = useState<any>(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);

  const onToggleSnackbar = (): void => {
    setIsSnackbarOpen(!isSnackbarOpen);
  };

  const setNewSnackbarMessage = (message: string): void => {
    setIsSnackbarOpen(true);
    setSnackbarMessage(message);
    setSnackbarError(false);
  };

  const setNewSnackbarError = (error: any): void => {
    setIsSnackbarOpen(true);
    setSnackbarError(error);
    setSnackbarMessage("");
  };

  const createUser = (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    company: string,
    phoneNumber: string
  ) => {
    auth
      .createUserWithEmailAndPassword(email, password)
      .then(async (user) => {
        const pending = await bootstrapNewUser({
          uid: user.user.uid,
          firstName,
          lastName,
          email,
          company,
          phoneNumber,
        });
        props.onNewUserCreated(pending);
      })
      .catch((error) => {
        setNewSnackbarError(error.message);
      });
  };

  const loginUser = (user) => {
    props.logUser(user);
  };

  const handleGoogleSignIn = (): Promise<void> => {
    const provider = new firebase.auth.GoogleAuthProvider();
    return firebase
      .auth()
      .signInWithPopup(provider)
      .then(async (result) => {
        if (result.additionalUserInfo?.isNewUser) {
          const { uid, email, displayName } = result.user;
          const [firstName, ...rest] = (displayName || "").trim().split(" ");
          const pending = await bootstrapNewUser({
            uid,
            firstName,
            lastName: rest.join(" "),
            email,
            company: "",
            phoneNumber: "",
          });
          props.onNewUserCreated(pending);
        } else {
          loginUser(result);
        }
      });
  };

  const changeTab = () => {
    setIsLoginOpen(!isLoginOpen);
  };

  const changeTabMessage = isLoginOpen
    ? "Don't have an account? Sign up"
    : "Already have an account? Sign in";

  return (
    <Page className="ds-root">
      <Stack>
        <LogoLetter src={images.logo_letters} alt="Tribed" />
        <Card>
          {isLoginOpen ? (
            <LoginForm
              loginUser={loginUser}
              setSnackbarError={setNewSnackbarError}
              onGoogleSignIn={handleGoogleSignIn}
            />
          ) : (
            <RegisterUserForm
              setSnackbarMessage={setNewSnackbarMessage}
              setSnackbarError={setNewSnackbarError}
              createUser={createUser}
              onGoogleSignIn={handleGoogleSignIn}
            />
          )}
          <ChangeTabButton type="button" onClick={changeTab}>
            {changeTabMessage}
          </ChangeTabButton>
        </Card>
        <PublicLinks aria-label="Public information">
          <a href="/tiktok">TikTok publishing</a>
          <a href="https://tribed.io/privacy">Privacy</a>
          <a href="https://tribed.io/terms">Terms</a>
          <a href="mailto:hello@tribed.io">Contact</a>
        </PublicLinks>
      </Stack>
      <Snackbar
        onCloseSnackbar={onToggleSnackbar}
        message={snackbarMessage}
        isOpen={isSnackbarOpen}
        error={snackbarError}
      />
    </Page>
  );
};

const mapDispatchToProps = (dispatch) => {
  return {
    logUser: (value) => dispatch({ type: "LOGGED_USER", payload: value }),
  };
};

export default connect(null, mapDispatchToProps)(Login);
